import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { POSTS } from "@/data/content";

const glowClass = { win: "glow-win", mac: "glow-mac", neutral: "glow-neutral" };
const accentText = { win: "text-[#0055FF]", mac: "text-[#FF3300]", neutral: "text-[#A1A1AA]" };

const CardImage = ({ post, tall }) => (
  <div className={`overflow-hidden ${tall ? "aspect-[16/9] md:aspect-[21/10]" : "aspect-[16/10]"}`}>
    <img
      src={post.image}
      alt={post.title}
      loading="lazy"
      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
  </div>
);

const CardBody = ({ post }) => (
  <div className="p-6 md:p-8">
    <div className="flex items-center justify-between mb-4">
      <span className={`font-oswald text-xs tracking-[0.3em] uppercase ${accentText[post.side]}`}>
        {post.category}
      </span>
      <span className="font-mono-p text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
        {post.readTime}
      </span>
    </div>
    <h3 className="font-serif-d text-2xl md:text-3xl leading-tight mb-3 group-hover:underline decoration-1 underline-offset-4">
      {post.title}
    </h3>
    <p className="font-mono-p text-sm leading-relaxed text-[#A1A1AA] line-clamp-3">{post.excerpt}</p>
    <div className="mt-6 flex items-center justify-between">
      <span className="font-mono-p text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">{post.date}</span>
      <ArrowUpRight className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#F5F5F5] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
    </div>
  </div>
);

const BlogGrid = () => (
  <section id="posts" className="py-28 md:py-36" data-testid="blog-grid-section">
    <div className="max-w-7xl mx-auto px-6">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <div>
          <p className="font-mono-p text-xs tracking-[0.35em] uppercase text-[#FF3300] mb-4">
            The Dispatches
          </p>
          <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            Six rounds. Six verdicts.
          </h2>
        </div>
        <p className="font-mono-p text-sm text-[#A1A1AA] max-w-sm leading-relaxed">
          Every category that actually decides the Windows-versus-Mac question,
          examined without mercy.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6">
        <Reveal className="md:col-span-2">
          <Link
            to={`/blog/${POSTS[0].slug}`}
            data-testid={`post-card-${POSTS[0].slug}`}
            className={`group block border border-white/10 bg-[#121212]/40 transition-shadow duration-500 ${glowClass[POSTS[0].side]}`}
          >
            <CardImage post={POSTS[0]} tall />
            <CardBody post={POSTS[0]} />
          </Link>
        </Reveal>

        {POSTS.slice(1, 5).map((post, i) => (
          <Reveal key={post.slug} delay={(i % 2) * 0.12} className={i % 2 === 1 ? "md:mt-12" : ""}>
            <Link
              to={`/blog/${post.slug}`}
              data-testid={`post-card-${post.slug}`}
              className={`group block border border-white/10 bg-[#121212]/40 transition-shadow duration-500 ${glowClass[post.side]}`}
            >
              <CardImage post={post} />
              <CardBody post={post} />
            </Link>
          </Reveal>
        ))}

        <Reveal className="md:col-span-2">
          <Link
            to={`/blog/${POSTS[5].slug}`}
            data-testid={`post-card-${POSTS[5].slug}`}
            className={`group grid md:grid-cols-2 border border-white/10 bg-[#121212]/40 transition-shadow duration-500 ${glowClass[POSTS[5].side]}`}
          >
            <div className="overflow-hidden aspect-[16/10] md:aspect-auto md:h-full">
              <img
                src={POSTS[5].image}
                alt={POSTS[5].title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
            <CardBody post={POSTS[5]} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default BlogGrid;
